import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import { db, isStudent, Student } from '../../../utils/firebase';
import { getAchievementConditionText, type AchievementType } from '../../../utils/achievementDisplay';

interface AchievementRow {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  type: AchievementType;
  isActive: boolean;
  isSecret: boolean;
  targetValue?: number;
}

const StudentAchievements: React.FC = () => {
  const { currentUser } = useAuth();
  const [list, setList] = useState<AchievementRow[]>([]);
  const [unlockedIds, setUnlockedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const student = currentUser && isStudent(currentUser) ? (currentUser as Student) : null;
  const schoolId = student?.schoolId;
  const classId = student?.classId;
  const uid = student?.uid;

  useEffect(() => {
    if (!schoolId || !classId) {
      setLoading(false);
      return;
    }

    const ref = collection(db, 'schools', schoolId, 'classes', classId, 'achievements');
    const unsub = onSnapshot(
      ref,
      (snapshot) => {
        const rows: AchievementRow[] = snapshot.docs
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              title: (data.title as string) || '',
              description: (data.description as string) || '',
              imageUrl: data.imageUrl as string | undefined,
              type: ((data.type as AchievementType) || 'point'),
              isActive: data.isActive !== false,
              isSecret: data.isSecret === true,
              targetValue: data.targetValue as number | undefined
            };
          })
          .filter((a) => a.isActive);
        setList(rows);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('업적 목록 오류:', err);
        setError('업적을 불러오지 못했습니다.');
        setLoading(false);
      }
    );
    return () => unsub();
  }, [schoolId, classId]);

  const idsKey = useMemo(() => list.map((a) => a.id).sort().join(','), [list]);

  useEffect(() => {
    if (!schoolId || !classId || !uid || !idsKey) return;
    const ids = idsKey.split(',').filter(Boolean);
    const unsubs = ids.map((achievementId) =>
      onSnapshot(
        doc(
          db,
          'schools',
          schoolId,
          'classes',
          classId,
          'achievements',
          achievementId,
          'awards',
          uid
        ),
        (snap) => {
          setUnlockedIds((prev) => {
            const next = new Set(prev);
            if (snap.exists()) {
              next.add(achievementId);
            } else {
              next.delete(achievementId);
            }
            return next;
          });
        }
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [schoolId, classId, uid, idsKey]);

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  if (!isStudent(currentUser)) {
    return <Navigate to="/" replace />;
  }
  if (!classId) {
    return (
      <div style={{ padding: '24px', fontFamily: 'Pretendard', color: 'var(--Color-text-normal)' }}>
        학급에 배정된 후 이용할 수 있습니다.
      </div>
    );
  }

  const isEarned = (a: AchievementRow) => unlockedIds.has(a.id);

  const showDetails = (a: AchievementRow) => !a.isSecret || isEarned(a);

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '960px',
        margin: '0 auto',
        padding: '24px 20px 48px',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--gap-level-4, 16px)',
        fontFamily: 'var(--font-family-primary, Pretendard, sans-serif)'
      }}
    >
      <div>
        <h1
          style={{
            margin: 0,
            fontSize: '22px',
            fontWeight: 600,
            lineHeight: '33px',
            color: 'var(--Color-text-strong, #0D0D0D)'
          }}
        >
          나의 업적
        </h1>
        <p
          style={{
            margin: '8px 0 0',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '21px',
            color: 'var(--Color-text-assistive-strong, #4C4C4C)'
          }}
        >
          달성 조건을 확인하고, 비공개 업적은 달성하면 트로피처럼 공개됩니다.
        </p>
      </div>

      {error && (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: '8px',
            background: '#FFEBEE',
            color: '#C62828',
            fontSize: '14px'
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--Color-text-assistive)' }}>
          불러오는 중…
        </div>
      ) : list.length === 0 ? (
        <div
          style={{
            padding: '48px 24px',
            textAlign: 'center',
            borderRadius: '16px',
            border: '1px dashed var(--Color-line-normal, #F0F0F0)',
            color: 'var(--Color-text-assistive)'
          }}
        >
          아직 등록된 업적이 없습니다.
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '16px'
          }}
        >
          {list.map((a) => {
            const earned = isEarned(a);
            const reveal = showDetails(a);
            const condition = getAchievementConditionText({
              type: a.type,
              targetValue: a.targetValue
            });

            return (
              <div
                key={a.id}
                style={{
                  borderRadius: '16px',
                  border: '1px solid var(--Color-line-normal, #F0F0F0)',
                  background: 'var(--Color-background-normal, #FFFFFF)',
                  boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div
                  style={{
                    height: '140px',
                    background: reveal
                      ? a.imageUrl
                        ? `url(${a.imageUrl}) center/cover`
                        : 'linear-gradient(145deg, #FFE8D6 0%, #FFD2B5 100%)'
                      : 'linear-gradient(145deg, #2A3142 0%, #1B2130 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}
                >
                  {!reveal && (
                    <span style={{ fontSize: '56px', filter: 'grayscale(1)', opacity: 0.85 }} aria-hidden>
                      🏆
                    </span>
                  )}
                  {reveal && !a.imageUrl && (
                    <span style={{ fontSize: '48px' }} aria-hidden>
                      🏆
                    </span>
                  )}
                  <div
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      fontSize: '11px',
                      fontWeight: 600,
                      background: earned ? 'rgba(255,111,15,0.95)' : 'rgba(0,0,0,0.45)',
                      color: '#fff'
                    }}
                  >
                    {earned ? '달성' : '미달성'}
                  </div>
                </div>

                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                  {!reveal ? (
                    <>
                      <div
                        style={{
                          fontSize: '17px',
                          fontWeight: 700,
                          color: 'var(--Color-text-strong)',
                          letterSpacing: '0.04em'
                        }}
                      >
                        ???
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--Color-text-assistive)', lineHeight: '20px' }}>
                        비공개 업적입니다. 달성하면 이름과 설명이 공개됩니다.
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '17px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
                        {a.title}
                      </div>
                      <div
                        style={{
                          fontSize: '13px',
                          color: 'var(--Color-text-normal)',
                          lineHeight: '20px',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {a.description}
                      </div>
                      <div
                        style={{
                          marginTop: '4px',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          background: 'var(--Color-background-main, #F4F6FA)',
                          fontSize: '12px',
                          fontWeight: 500,
                          color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                          lineHeight: '18px'
                        }}
                      >
                        <span style={{ color: 'var(--Color-fill-brand-color, #FF6F0F)', fontWeight: 600 }}>달성 조건</span>
                        <br />
                        {condition}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StudentAchievements;
